import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ModulePane } from '../components/ModulePane/ModulePane';
import { AccessNotice } from '../components/AccessNotice/AccessNotice';
import { isAccessDenied } from '../lib/dataverse';
import { useCurrentUser, useCan, useTeamDisplayName } from '../lib/identity';
import { fetchCorrespondence, markCopiedToActuris, type Correspondence } from '../lib/compose';
import styles from './CorrespondenceDetail.module.css';

function fmtDateTime(iso?: string): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

const STATUS_LABEL: Record<string, string> = {
  Draft: 'Draft',
  CopiedToActuris: 'Copied to Acturis',
  Sent: 'Sent',
  WrittenToActuris: 'Written to Acturis',
};

export function CorrespondenceDetail() {
  const { ref } = useParams<{ ref: string }>();
  const user = useCurrentUser();
  const canCompose = useCan('compose');
  const displayNameFor = useTeamDisplayName();
  const queryClient = useQueryClient();

  const [copied, setCopied] = useState(false);
  const [marking, setMarking] = useState(false);
  const [actionError, setActionError] = useState('');

  const q = useQuery({
    queryKey: ['compose.correspondence.detail', ref],
    queryFn: () => fetchCorrespondence(ref ?? ''),
    enabled: !!ref && !!user,
  });
  const co: Correspondence | null = q.data ?? null;

  const handleCopy = async () => {
    if (!co?.gaqc_renderedbody) return;
    try {
      await navigator.clipboard.writeText(co.gaqc_renderedbody);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setActionError('Copy failed — select the text and copy manually.');
    }
  };

  const handleMarkCopied = async () => {
    if (!co || !user) return;
    setMarking(true);
    setActionError('');
    try {
      await markCopiedToActuris(co, user.upn);
      await q.refetch();
      // keep the list view's status fresh too
      queryClient.invalidateQueries({ queryKey: ['compose.correspondence'] });
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'Failed to update status.');
    } finally {
      setMarking(false);
    }
  };

  const sidebar = (
    <>
      <Link to="/" className={styles.back}>← Back to drafts</Link>
      {co && (
        <div className={styles.metaCard}>
          <div className={styles.metaRow}><span>Ref</span><strong className={styles.mono}>{co.gaqc_correspondenceref}</strong></div>
          <div className={styles.metaRow}><span>Kind</span><strong>{co.gaqc_kind}</strong></div>
          <div className={styles.metaRow}><span>Client</span><strong>{co.gaqc_clientname || '—'}</strong></div>
          <div className={styles.metaRow}><span>Policy year</span><strong className={styles.mono}>{co.gaqc_policyyearref || '—'}</strong></div>
          <div className={styles.metaRow}><span>Template</span><strong className={styles.mono}>{co.gaqc_templateref || '—'}</strong></div>
          <div className={styles.metaRow}><span>Created by</span><strong>{co.gaqc_createdbyupn ? displayNameFor(co.gaqc_createdbyupn) : '—'}</strong></div>
          <div className={styles.metaRow}><span>Created</span><strong>{fmtDateTime(co.gaqc_createdatutc)}</strong></div>
        </div>
      )}
    </>
  );

  return (
    <ModulePane sidebar={sidebar} ariaLabel="SODAN detail">
      {q.isLoading && <div className={styles.state}>Loading…</div>}
      {q.isError &&
        (isAccessDenied(q.error) ? (
          <AccessNotice />
        ) : (
          <div className={styles.errorBox}>Couldn’t load this draft. {String(q.error)}</div>
        ))}
      {q.isSuccess && !co && (
        <div className={styles.empty}>
          No correspondence found for <code>{ref}</code>. It may have been removed.
        </div>
      )}

      {co && (
        <>
          <header className={styles.header}>
            <div>
              <h1 className={styles.title}>{co.gaqc_clientname || co.gaqc_correspondenceref}</h1>
              <p className={styles.lead}>
                {co.gaqc_kind} ·{' '}
                <span className={`${styles.statusChip} ${styles[`status_${co.gaqc_status}`] ?? ''}`}>
                  {STATUS_LABEL[co.gaqc_status ?? ''] ?? co.gaqc_status}
                </span>
              </p>
            </div>
            <div className={styles.actions}>
              <button type="button" className={styles.copyBtn} onClick={handleCopy}>
                {copied ? '✓ Copied' : 'Copy'}
              </button>
              {canCompose && co.gaqc_status === 'Draft' && (
                <button type="button" className={styles.markBtn} onClick={handleMarkCopied} disabled={marking}>
                  {marking ? 'Updating…' : 'Mark as copied to Acturis'}
                </button>
              )}
            </div>
          </header>

          {actionError && <div className={styles.errorBox}>{actionError}</div>}

          {co.gaqc_copiedtoacturisatutc && (
            <div className={styles.copiedNote}>
              ✓ Copied to Acturis on {fmtDateTime(co.gaqc_copiedtoacturisatutc)}
              {co.gaqc_copiedtoacturisbyupn ? ` by ${displayNameFor(co.gaqc_copiedtoacturisbyupn)}` : ''}.
            </div>
          )}

          <pre className={styles.body}>{co.gaqc_renderedbody}</pre>
        </>
      )}
    </ModulePane>
  );
}
